import NiceModal from '@ebay/nice-modal-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router';
import { type ChangeOrderRefType, getChangeOrderApprovals } from '@/api/Sherpa/ChangeOrder';
import { SherpaProjectReactQueryKey } from '@/constants/ReactQueryKeys';
import ChangeOrderDetails from '@/features/ChangeOrder/Details';
import { EditDeliverableModal } from '@/features/ChangeOrder/Summary/ChangeOrderDeliverables/EditDeliverable';
import { useDeliverableChangeOrderDetails } from '@/hooks/ReactQueryHooks/useChangeOrderDetailsQuery';
import { formatDate3 } from '@/utils';

export function ChangeOrderDeliverableDetails() {
  const { projectId = '', changeOrderId = '' } = useParams();
  const queryClient = useQueryClient();
  const { data } = useDeliverableChangeOrderDetails({
    id: Number(changeOrderId),
  });
  const resourceType: ChangeOrderRefType = 'changeOrderDeliverable';

  const { data: approvals } = useQuery(
    [SherpaProjectReactQueryKey.GET_CHANGE_ORDER_APPROVALS, changeOrderId],
    () =>
      getChangeOrderApprovals({
        refType: resourceType,
        refId: Number(changeOrderId),
      }),
    {
      select: (data) => data.data,
    }
  );

  const invalidateQueries = () => {
    queryClient.invalidateQueries([SherpaProjectReactQueryKey.GET_CHANGE_ORDER_DELIVERABLES, Number(changeOrderId)]);
    queryClient.invalidateQueries([SherpaProjectReactQueryKey.GET_CHANGE_ORDER_APPROVALS]);
    queryClient.invalidateQueries([`${resourceType}-audit`]);
  };

  const handleEdit = () => {
    if (!data) return;

    NiceModal.show(EditDeliverableModal, {
      projectId,
      changeOrderId,
      defaultValues: {
        phase: {
          label: data.phase.name,
          value: data.phase.slug,
        },
        deliverable: {
          value: String(data.cOrderTypeId),
          label: data.cOrderTypeName,
          dueDate: data.deliverable.originalDueDate,
        },
        dueDate: formatDate3(data.deliverable.originalDueDate, 'yyyy-mm-dd'),
        proposedDueDate: formatDate3(data?.deliverable.proposedDueDate, 'yyyy-mm-dd'),
        changeInDays: data.daysImpacted,
        noResponseConsequence: data.consequences,
        notes: data.notes,
        reason: data.reason,
        responseDueBy: formatDate3(data.deliverable.responseDueByDate, 'yyyy-mm-dd'),
        recentFiles: approvals?.at(0)?.approvalAsset,
      },
      onSubmitted: invalidateQueries,
    });
  };

  return (
    <ChangeOrderDetails
      title={data?.cOrderTypeName}
      type='deliverable'
      resourceType={resourceType}
      commentResourceType='ChangeOrderDeliverable'
      onEdit={handleEdit}
      status={data?.status || ''}
      submittedBy={data?.submittedBy}
      additionalInfo={[
        {
          title: 'Why is the change required?',
          value: data?.reason || '',
        },
        {
          title: 'No response consequence',
          value: data?.consequences || '',
        },
        {
          title: 'Notes',
          value: data?.notes || '–',
        },
      ]}
      details={[
        {
          title: 'Original Due Date',
          value: data?.deliverable?.originalDueDate ? formatDate3(data?.deliverable?.originalDueDate) : '',
        },
        {
          title: 'Response Due Date',
          value: data?.deliverable?.responseDueByDate ? formatDate3(data?.deliverable?.responseDueByDate) : '',
        },
        {
          title: 'Proposed Due Date',
          value: data?.deliverable?.proposedDueDate ? formatDate3(data?.deliverable?.proposedDueDate) : '',
        },
        {
          title: 'Days Left to Respond',
          value: String(data?.daysLeft) || '',
        },
        {
          title: '',
          value: '',
        },
        {
          title: 'Impact',
          value: `${data?.daysImpacted} Days`,
        },
      ]}
    />
  );
}
